import { useDashboardReview } from '../hooks/useDashboardReview'
import { ReviewLoadingState } from '../components/DashboardReview/ReviewLoadingState'
import React, { useMemo, useRef, useState } from 'react'
import keys from 'constants/keys'
import { RootState, Task } from 'constants/interfaces'
import { BaseModalV2 } from 'components/ui/BaseModal'
import { ExecutionHeader } from '../components/ExecutionHeader'
import { useExecutionText } from '../hooks/useExecutionText'
import { Flex } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { Button } from 'components/ui'
import { isRtlSelector } from 'redux/selectors/general'
import { useStepDirection } from 'components/TasksV2/UI/SlideInPanel'
import { SkipSubtaskButton } from 'components/TasksV2/UI/SkipSubtaskButton'
import { ExecutionFrame } from '../components/ExecutionFrame'
import { useMediaSubtaskPager } from '../useMediaSubtaskPager'
import { ExecutionWorkspace } from '../components/ExecutionWorkspace'
import VotesOverview from '../Content/PollOverview'
import {
  TaskOverviewStackProvider,
  TaskOverviewContentType,
  ITaskOverviewContentStack,
  useTaskOverviewContext,
} from '../TaskOverviewStackProvider'
import ModalMediaSection from '../Content/MediaSubtasksCompleting'
import QuestionsOverview from '../Content/QuestionsOverview'

interface IProps {
  task: Task
  onClose: () => void
  content?: ITaskOverviewContentStack
  isComeFromDashboard?: boolean
}

const TaskOverviewModalContent: React.FC<IProps> = ({ task, onClose, isComeFromDashboard = false }) => {
  const { contentStack, closeContent, isTaskCompleted, isSubtaskCompleted, resultContextId, isReadMode } =
    useTaskOverviewContext()
  const t = useExecutionText()
  const review = useDashboardReview()
  const { isLoading } = useSelector((state: RootState) => state.tasks_v2)

  const [navigationSlot, setNavigationSlot] = useState<HTMLDivElement | null>(null)
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null)
  const initialFocusRef = useRef<HTMLDivElement>(null)
  const pager = useMediaSubtaskPager()
  const rtl = useSelector(isRtlSelector)
  const transition = useStepDirection(contentStack.length)

  const currentContent = contentStack[contentStack.length - 1]

  // Key identifying the active content frame — feeds the modal's scroll memory so
  // navigating into a subtask and back restores each frame's own scroll position.
  const activeFrameKey = `${currentContent?.contentType ?? ''}:${currentContent?.props?.selectedSubtask?.st_id ?? ''}`

  const isWorkspace = currentContent?.contentType === TaskOverviewContentType.MAIN

  const isMediaExecution = currentContent?.contentType === TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING

  const headerTitle = useMemo(() => {
    if (!currentContent) return null
    const { contentType, props } = currentContent
    switch (contentType) {
      case TaskOverviewContentType.MAIN:
        return task.title
      case TaskOverviewContentType.VOTES_OVERVIEW:
      case TaskOverviewContentType.OPEN_QUESTION_OVERVIEW:
        return props?.title
      case TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING:
        return props.selectedSubtask.title
      default:
        return null
    }
  }, [currentContent, task.title])

  const isDoneBtnDisabled = useMemo(() => {
    if (currentContent?.contentType === TaskOverviewContentType.MAIN) {
      return !isTaskCompleted
    }
    if (currentContent?.contentType === TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING) {
      return currentContent.props?.selectedSubtask?.is_required && !isSubtaskCompleted
    }
    return false
  }, [currentContent, isSubtaskCompleted, isTaskCompleted])

  const doneAction = !isReadMode ? (
    <Button
      className="execution-done"
      variant="primary"
      onClick={closeContent}
      isDisabled={isLoading || isDoneBtnDisabled || pager.isBusy}
      borderRadius="10px"
      px="32px"
      minW="120px"
      h="40px"
      _focus={{ boxShadow: 'none' }}
      _hover={{ opacity: 0.8 }}
    >
      {t.done}
    </Button>
  ) : null

  return (
    <BaseModalV2
      floating
      modalProps={{ closeOnEsc: false, closeOnOverlayClick: false, initialFocusRef }}
      modalContentProps={
        isMediaExecution || isWorkspace
          ? {
              ref: initialFocusRef,
              tabIndex: -1,
              maxW: 'none',
              minW: 0,
              className: 'floating-media-modal task-execution-modal',
            }
          : { ref: initialFocusRef, tabIndex: -1, w: '94vw', maxW: '880px', minW: 0, className: 'task-execution-modal' }
      }
      modalHeaderProps={{ px: 0, py: '2px', ...(review ? { style: { overflow: 'visible' } } : {}) }}
      modalBodyProps={
        isMediaExecution || isWorkspace
          ? {
              maxH: 'none',
              height: '100%',
              flex: 1,
              minH: 0,
              overflowY: 'hidden',
              overflowX: 'hidden',
              px: 0,
              py: 0,
            }
          : { maxH: '70vh' }
      }
      scrollMemoryKey={activeFrameKey}
      isOpen={true}
      onClose={onClose}
      modalFooterProps={{ alignItems: 'center', justifyContent: 'center', bg: isComeFromDashboard ? '#f4f4f4' : '' }}
      header={
        <ExecutionHeader
          actionsRef={setHeaderSlot}
          navigationRef={setNavigationSlot}
          title={headerTitle}
          subtask={isMediaExecution ? currentContent.props?.selectedSubtask : undefined}
        />
      }
      body={
        <>
          {review && <ReviewLoadingState media={isMediaExecution} />}
          <div
            style={{
              height: '100%',
              minHeight: 0,
              position: 'relative',
              display: review?.loading ? 'none' : 'contents',
            }}
          >
            {contentStack.map((content, index) => {
              const { contentType, props } = content
              const isTopContent = index === contentStack.length - 1

              switch (contentType) {
                case TaskOverviewContentType.MAIN:
                  return (
                    <ExecutionFrame
                      active={isTopContent}
                      animate={transition.hasNavigated}
                      direction={transition.direction}
                      rtl={rtl}
                      key={`main-${index}`}
                    >
                      <ExecutionWorkspace
                        active={isTopContent}
                        headerSlot={headerSlot}
                        navigationSlot={navigationSlot}
                      />
                    </ExecutionFrame>
                  )
                case TaskOverviewContentType.VOTES_OVERVIEW:
                  return (
                    props?.selectedSubtask && (
                      <ExecutionFrame
                        active={isTopContent}
                        animate={transition.hasNavigated}
                        direction={transition.direction}
                        rtl={rtl}
                        key={`${index}-${content}`}
                      >
                        <VotesOverview pollSubtask={props.selectedSubtask} />
                      </ExecutionFrame>
                    )
                  )
                case TaskOverviewContentType.MEDIA_SUBTASK_COMPLETING:
                  return (
                    props?.selectedSubtask && (
                      <ExecutionFrame
                        active={isTopContent}
                        animate={transition.hasNavigated}
                        direction={transition.direction}
                        rtl={rtl}
                        key={`media-${props.selectedSubtask.st_id}`}
                      >
                        <ModalMediaSection
                          executionFooter={
                            <Flex
                              className="execution-media-footer-content"
                              width="100%"
                              justifyContent="center"
                              alignItems="center"
                            >
                              <span className="execution-media-footer-skip">
                                <SkipSubtaskButton
                                  isRequired={props.selectedSubtask.is_required}
                                  isSkipped={pager.status === keys.TASK_STATUSES.SKIPPED.value}
                                  isResult={isSubtaskCompleted}
                                  subtaskType={props.selectedSubtask.type}
                                  tid={task.tid}
                                  st_id={props.selectedSubtask.st_id}
                                  resultContextId={resultContextId}
                                />
                              </span>
                              {doneAction}
                            </Flex>
                          }
                          subtask={props.selectedSubtask}
                          resultContextId={resultContextId}
                          isReadMode={isReadMode}
                          recurrence_ts={task.recurrence_ts}
                          retail_id_context_id={task.retail_id_context_id}
                          is_required_approval={task.is_required_approval}
                          initialMediaIndex={props.targetMediaIndex}
                          targetParentCommentId={props.targetParentCommentId}
                          targetCommentId={props.targetCommentId}
                        />
                      </ExecutionFrame>
                    )
                  )
                case TaskOverviewContentType.OPEN_QUESTION_OVERVIEW:
                  return (
                    props?.selectedSubtask && (
                      <ExecutionFrame
                        active={isTopContent}
                        animate={transition.hasNavigated}
                        direction={transition.direction}
                        rtl={rtl}
                        key={`${index}-${content}`}
                      >
                        <QuestionsOverview questionSubtask={props.selectedSubtask} />
                      </ExecutionFrame>
                    )
                  )
                default:
                  return null
              }
            })}
          </div>
        </>
      }
      footer={isMediaExecution || isWorkspace ? <></> : doneAction ?? <></>}
    />
  )
}

export const TaskOverviewModal: React.FC<IProps> = (props) => {
  return (
    <TaskOverviewStackProvider
      key={`${props.task.tid}:${props.task.dashboardContextId || ''}:${props.task.recurrence_ts || ''}`}
      task={props.task}
      content={props.content}
      isComeFromDashboard={props.isComeFromDashboard}
      onClose={props.onClose}
    >
      <TaskOverviewModalContent {...props} />
    </TaskOverviewStackProvider>
  )
}
